<template>
  <zaproo-sidebar
    class="filters-sidebar"
    :class="[!sidebarContentInt?.hasMobile ? '!h-auto' : '']"
  >
    <!--FILTER SIDEBAR HEADING-->
    <zaproo-sidebar-heading class="!px-4 !py-3" :has-button="false">
      <span v-if="!selectedFilterCount" class="font-semibold"
        >{{ $t("Filters") }}
      </span>
      <span v-else>
        <span class="font-semibold"> {{ $t("Filters") }}:</span>
        <span class="ml-1 font-bold">{{ selectedFilterCount }}</span>

        <sf-button
          class="ml-2"
          size="sm"
          variant="tertiary"
          aria-label="clear all button"
          @click="clearAllFilters()"
          >{{ $t("Clear all") }}</sf-button
        >
      </span>
      <template #button>
        <sf-button
          variant="tertiary"
          aria-label="close filters sidebar button"
          :size="SfButtonSize.sm"
          @click="sidebarContentInt?.closeSidebar()"
        >
          <sf-icon-close class="text-zinc-500" />
        </sf-button>
      </template>

      <selected-filters-chips
        class="pt-3.5"
        :editable-filter-manager="filterManager"
        :read-filter-manager="appliedFilterManager || filterManager"
        @on-filter-reset="$emit('onApplyFilters')"
      />
    </zaproo-sidebar-heading>

    <!--FILTER SIDEBAR CONTENT-->
    <zaproo-sidebar-content
      class="!p-0"
      :disable-body-scroll="
        !!sidebarContentInt?.hasMobile &&
        !!sidebarContentInt.hasMobileSidebarOpened
      "
    >
      <zaproo-accordion>
        <filter-portal
          :filter-manager="filterManager"
          :mode="mode"
          :category="category"
          :filter-wrapper="FilterSidebarWrapper"
        />
      </zaproo-accordion>

      <!--      <filter-accordion-skeleton v-else />-->
    </zaproo-sidebar-content>

    <!--FILTER SIDEBAR FOOTER-->
    <zaproo-sidebar-footer class="z-[1]">
      <div class="flex justify-between gap-2">
        <sf-button
          variant="secondary"
          aria-label="clear all button"
          class="w-full"
          @click="clearAllFilters()"
        >
          {{ $t("Clear all") }}
        </sf-button>
        <sf-button
          class="w-full"
          aria-label="apply filters button"
          @click="applyAllFilters"
        >
          {{ $t("Apply Filters") }}
        </sf-button>
      </div>
    </zaproo-sidebar-footer>
  </zaproo-sidebar>
</template>

<script setup lang="ts">
import { SfButtonSize } from "@zaprooecommerce/storefront-ui-vue";
import type { DeepReadonly } from "vue";
import type { SidebarContentInstance } from "~/modules/helpers/components/SidebarContent/types";
import type { IFilterManager } from "~/modules/catalog/classes/IFilterManager";
import SelectedFiltersChips from "~/modules/catalog/components/SelectedFiltersChips.vue";
import type { FiltersMode } from "~/modules/catalog/components/Filter/types";
import type { CategoryFull } from "~/types/category";
import FilterSidebarWrapper from "~/modules/catalog/components/FilterSidebarWrapper.vue";

const props = defineProps<{
  sidebarContentInt?: SidebarContentInstance;
  /** Filter manager to change and apply new filters **/
  filterManager: IFilterManager;
  /** Current applied filter manager, for display applied filters count **/
  appliedFilterManager?: DeepReadonly<IFilterManager>;
  /** Current filter mode will proxy to each filter component **/
  mode?: FiltersMode;
  /** Current categories path, allowed only on catalog page **/
  category?: CategoryFull | null;
  loading: boolean;
}>();

const emit = defineEmits<{
  (_event: "onApplyFilters"): void;
}>();

const selectedFilterCount = computed(
  () =>
    (props.appliedFilterManager || props.filterManager).selectedFilters()
      .length,
);

function clearAllFilters(): void {
  props.filterManager.reset();
  emit("onApplyFilters");
}

function applyAllFilters() {
  if (props.sidebarContentInt?.hasMobile) {
    props.sidebarContentInt?.closeSidebar();
  }
  emit("onApplyFilters");
}
</script>

<style lang="scss">
.filters-sidebar {
  @apply lg:w-full min-[1340px]:relative #{!important};

  .zaproo-sidebar {
    &-content {
      @apply pb-4;
    }
  }
}
</style>
